import { APP_CONFIG } from '@/constants/app';
import { useOrganisationsStore } from '@/store/organisations.store';
import { ApiResponse, AuthResponse, TokenPair } from '@/types/auth.types';
import axios, { AxiosInstance, AxiosRequestConfig } from 'axios';

class TokenManager {
  private isRefreshing = false;
  private refreshSubscribers: Array<{
    resolve: (token: string) => void;
    reject: (err: unknown) => void;
  }> = [];

  /**
   * Get current tokens from localStorage
   */
  getTokens(): TokenPair | null {
    const accessToken = localStorage.getItem('accessToken');
    const refreshToken = localStorage.getItem('refreshToken');
    const expiresIn = localStorage.getItem('expiresIn');
    const tokenType = localStorage.getItem('tokenType');

    if (!accessToken || !refreshToken) {
      return null;
    }

    return {
      accessToken,
      refreshToken,
      expiresIn: expiresIn ? parseInt(expiresIn) : 0,
      tokenType: tokenType || 'Bearer',
    };
  }

  /**
   * Save tokens to localStorage
   */
  saveTokens(tokens: TokenPair): void {
    localStorage.setItem('accessToken', tokens.accessToken);
    localStorage.setItem('refreshToken', tokens.refreshToken);
    localStorage.setItem('expiresIn', tokens.expiresIn.toString());
    localStorage.setItem('tokenType', tokens.tokenType);
  }

  /**
   * Clear all tokens from localStorage
   */
  clearTokens(): void {
    localStorage.removeItem('accessToken');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('expiresIn');
    localStorage.removeItem('tokenType');
  }

  /**
   * Refresh access token using refresh token
   */
  private async refreshAccessToken(): Promise<string> {
    const tokens = this.getTokens();
    if (!tokens?.refreshToken) {
      throw new Error('No refresh token available');
    }

    try {
      const response = await axios.post<ApiResponse<AuthResponse>>(
        `${APP_CONFIG.api.baseUrl}/auth/refresh`,
        { refreshToken: tokens.refreshToken },
        {
          headers: {
            'Content-Type': 'application/json',
          },
        }
      );

      const newTokens = response.data.data.tokens;
      this.saveTokens(newTokens);

      return newTokens.accessToken;
    } catch (error) {
      // If refresh fails, clear tokens and redirect to login
      this.clearTokens();
      window.location.href = '/login';
      throw error;
    }
  }

  /**
   * Add subscriber to refresh token process
   */
  private subscribeTokenRefresh(
    resolve: (token: string) => void,
    reject: (err: unknown) => void
  ): void {
    this.refreshSubscribers.push({ resolve, reject });
  }

  /**
   * Notify all subscribers with new token
   */
  private onTokenRefreshed(token: string): void {
    this.refreshSubscribers.forEach(sub => sub.resolve(token));
    this.refreshSubscribers = [];
  }

  /**
   * Notify all subscribers that the refresh FAILED so their requests are
   * rejected (sinon elles restent en attente pour toujours → spinners infinis).
   */
  private onTokenRefreshFailed(err: unknown): void {
    this.refreshSubscribers.forEach(sub => sub.reject(err));
    this.refreshSubscribers = [];
  }

  /**
   * Get current language for API requests
   */
  private getCurrentLanguage(): string {
    // Get language from localStorage (i18next stores it there)
    const storedLanguage = localStorage.getItem('i18nextLng');

    // Fallback to browser language or default
    if (!storedLanguage) {
      const browserLanguage = navigator.language?.split('-')[0];
      return ['fr', 'en'].includes(browserLanguage) ? browserLanguage : 'fr';
    }

    // Ensure it's a supported language
    return ['fr', 'en'].includes(storedLanguage) ? storedLanguage : 'fr';
  }

  /**
   * Setup axios interceptors for automatic token refresh
   */
  setupInterceptors(axiosInstance: AxiosInstance): void {
    // Request interceptor - add auth token and language headers
    axiosInstance.interceptors.request.use(
      config => {
        const tokens = this.getTokens();
        if (tokens?.accessToken) {
          config.headers.Authorization = `${tokens.tokenType} ${tokens.accessToken}`;
        }

        // Add language headers
        const currentLanguage = this.getCurrentLanguage();
        config.headers['Accept-Language'] = currentLanguage;
        config.headers['Content-Language'] = currentLanguage;

        // Inject current organisation UUID as header (not query param to avoid DTO validation issues)
        const orgUuid = useOrganisationsStore.getState().currentOrganisation?.uuid;
        if (orgUuid) {
          config.headers['x-organisation-uuid'] = orgUuid;
        }

        return config;
      },
      error => Promise.reject(error)
    );

    // Response interceptor - handle token refresh
    axiosInstance.interceptors.response.use(
      response => response,
      async error => {
        const originalRequest = error.config as AxiosRequestConfig & {
          _retry?: boolean;
        };

        // Détection ROBUSTE d'un token expiré/invalide.
        // - code machine stable `SESSION_EXPIRED` (nouveau backend)
        // - statut HTTP 401 (sémantique correcte)
        // - compat : ancien 400 + clé i18n dans `message`
        const status = error.response?.status;
        const body = error.response?.data;
        const hasRefreshToken = !!this.getTokens()?.refreshToken;
        const isSessionError =
          body?.code === 'SESSION_EXPIRED' ||
          status === 401 ||
          (status === 400 &&
            ['auths.session_expired', 'auths.invalid_token'].includes(
              body?.message
            ));

        if (isSessionError && hasRefreshToken && !originalRequest._retry) {
          if (this.isRefreshing) {
            // If already refreshing, wait for it to complete (ou échouer).
            return new Promise((resolve, reject) => {
              this.subscribeTokenRefresh(
                (token: string) => {
                  originalRequest.headers = originalRequest.headers || {};
                  originalRequest.headers.Authorization = `Bearer ${token}`;
                  resolve(axiosInstance(originalRequest));
                },
                (err: unknown) => reject(err)
              );
            });
          }

          originalRequest._retry = true;
          this.isRefreshing = true;

          try {
            const newAccessToken = await this.refreshAccessToken();
            this.isRefreshing = false;
            this.onTokenRefreshed(newAccessToken);

            // Retry original request with new token
            originalRequest.headers = originalRequest.headers || {};
            originalRequest.headers.Authorization = `Bearer ${newAccessToken}`;
            return axiosInstance(originalRequest);
          } catch (refreshError) {
            this.isRefreshing = false;
            // Rejeter les requêtes en attente pour éviter les spinners infinis.
            this.onTokenRefreshFailed(refreshError);
            return Promise.reject(refreshError);
          }
        }

        return Promise.reject(error);
      }
    );
  }
}

// Export singleton instance
export const tokenManager = new TokenManager();
